/**
 * Marketplace helpers for the CRA marketplace at rivieratech.solutions/limelite/marketplace.
 *
 * Ported from apps/web/src/lib/marketplace.ts. The CRA app is the canonical marketplace
 * surface as of 2026-08-10; the Next implementation at apps/web is frozen. The pure
 * functions below (slugHue, listingImage, displayHost, compactCount) were the genuinely
 * good part of that build and are carried over unchanged in behaviour so a listing keeps
 * the same identity colour across both surfaces while they coexist.
 */

export const CATEGORIES = [
  { id: "all", label: "All" },
  { id: "ai", label: "AI" },
  { id: "saas", label: "SaaS" },
  { id: "developer-tool", label: "Dev Tools" },
  { id: "productivity", label: "Productivity" },
  { id: "ecommerce", label: "eCommerce" },
  { id: "mobile", label: "Mobile" },
  { id: "other", label: "Other" },
];

export const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.label]));

export const PLATFORMS = [
  { id: "all", label: "All platforms" },
  { id: "web", label: "Web" },
  { id: "android", label: "Android" },
  { id: "ios", label: "iOS" },
  { id: "cross-platform", label: "Cross-platform" },
];

export const PAGE_SIZE = 24;

/**
 * Best available card image, in Steam's order of preference: the purpose-cut capsule
 * first, then a real screenshot, then the icon, then nothing (and the caller draws a
 * generated tile). Bespoke art always wins over an automated capture.
 */
export function listingImage(listing) {
  return listing?.capsuleUrl || listing?.screenshotUrls?.[0] || listing?.iconUrl || null;
}

/** Full-bleed banner for the spotlight and detail header. Falls back to card art. */
export function listingHero(listing) {
  return listing?.heroUrl || listing?.capsuleUrl || listing?.screenshotUrls?.[0] || null;
}

/**
 * The frames a card cycles through on hover. Capsule first so the resting image is
 * frame 0 and the transition into frame 1 reads as motion rather than a swap.
 * Returns [] when there is nothing to cycle — the caller must not animate one frame.
 */
export function listingFrames(listing) {
  const frames = [];
  if (listing?.capsuleUrl) frames.push(listing.capsuleUrl);
  for (const s of listing?.screenshotUrls || []) if (!frames.includes(s)) frames.push(s);
  return frames.length > 1 ? frames : [];
}

/** Strip the scheme and any trailing slash — "maya.rivieratech.solutions". */
export function displayHost(appUrl) {
  if (!appUrl) return "";
  return appUrl.replace(/^https?:\/\//, "").replace(/\/+$/, "");
}

/**
 * Deterministic hue (0-359) from a slug, so every app has a stable identity colour and
 * the no-screenshot fallback looks designed rather than broken. Same algorithm as the
 * Next build — do not "improve" it, or existing apps change colour.
 */
export function slugHue(slug) {
  let h = 0;
  const s = String(slug || "");
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) % 360;
  }
  return h;
}

/** "2.4K" / "968" — compact counts for card footers. */
export function compactCount(n) {
  const v = n || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(v);
}

/**
 * True when a listing is still carrying the placeholder metadata that
 * marketplaceAutoListService stamps on auto-created entries. These taglines are the
 * single biggest reason the directory reads as a debug dump, so the UI treats them as
 * "no tagline" rather than rendering the same sentence on a dozen cards.
 */
const PLACEHOLDER_TAGLINES = new Set([
  "shop now on limelite",
  "built and deployed on limelite",
  "discover this creator on limelite",
]);

export function isPlaceholderTagline(tagline) {
  return PLACEHOLDER_TAGLINES.has(String(tagline || "").trim().toLowerCase());
}

/** The tagline worth showing, or "" when all we have is boilerplate. */
export function realTagline(listing) {
  const t = listing?.tagline || "";
  return isPlaceholderTagline(t) ? "" : t;
}
